import { ref, unref, watch, type Ref } from 'vue';
import { useSharedLink, type AdminPage, type PageMetafields } from '@shopexperts/shared';

export interface PageMetafieldsPreviewPayload {
  path: string;
  page: AdminPage | null;
  metafields: PageMetafields;
}

export interface PagePreviewLocationEvent {
  path?: string;
  url?: string;
}

function pathFromPreviewUrl(value?: string): string {
  if (!value) return '';
  try {
    return new URL(value, window.location.origin).pathname || '';
  } catch {
    return value.startsWith('/') ? value : `/${value}`;
  }
}

export function usePageMetafieldsPreview(
  iframeRef: Ref<HTMLIFrameElement | null>,
  payload: Ref<PageMetafieldsPreviewPayload>,
  onPreviewPathChange: (path: string) => void,
  targetOrigin: string | Ref<string>,
) {
  const revision = ref(0);
  const previewLink = useSharedLink<PageMetafieldsPreviewPayload | null>({
    name: 'admin-preview-metafields',
    iframeRef,
    initial: payload,
    targetOrigin,
    debounceMs: 25,
    debug: false,
  });

  watch(() => `${payload.value.path}:${payload.value.page?.id ?? 'new'}`, () => {
    revision.value += 1;
  });

  function syncFrame() {
    try {
      iframeRef.value?.contentWindow?.postMessage({ type: 'is_admin_preview' }, unref(targetOrigin));
    } catch {}
    previewLink.send(payload.value);
  }

  function handlePreviewLocation(payload?: PagePreviewLocationEvent) {
    const path = payload?.path || pathFromPreviewUrl(payload?.url);
    if (path) onPreviewPathChange(path);
  }

  previewLink.onEvent<PagePreviewLocationEvent>('preview-loaded', handlePreviewLocation);
  previewLink.onEvent<PagePreviewLocationEvent>('preview-page-changed', handlePreviewLocation);

  return {
    previewRevision: revision,
    previewConnected: previewLink.connected,
    syncFrame,
  };
}
